import React, { useRef, useState } from "react";
import style from "../../DetailContainer.module.scss";

import { GrDocumentText } from "react-icons/gr";
import { LuDownload, LuUpload } from "react-icons/lu";
import { MdOutlineFileUpload } from "react-icons/md";

import { MyModal } from "../../../Common/Model/MyModal";
import ModalContainer from "../../../Common/Model/ModalContainer";
import SuccessComponent from "../../../Common/Success/SuccessComponent";

import { uploadCandidateResume } from "../../../../services/candidateServices";
import { Date_Date } from "../../../../utility";

const MAX_FILE_SIZE_MB = 1.5;

const ALLOWED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/rtf",
];

function CandidateResume({ id, data = [], refreshResume }) {
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  const [candidateName, setCandidateName] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  // 🔥 Upload button click
  const handleUploadClick = () => {
    if (!id) {
      alert("Candidate ID missing");
      return;
    }
    fileInputRef.current?.click();
  };

  // 🔥 VALIDATE + API CALL
  const validateAndUpload = async (file) => {
    if (!file) {
      alert("Please select a file");
      return;
    }

    const sizeInMB = file.size / (1024 * 1024);

    // ✅ Size validation
    if (sizeInMB > MAX_FILE_SIZE_MB) {
      alert("File size must be less than 1.5 MB");
      return;
    }

    // ✅ Type validation
    if (!ALLOWED_TYPES.includes(file.type)) {
      alert("Only PDF, DOC, DOCX, RTF files allowed");
      return;
    }

    setCandidateName(file.name);
    setShowUploadModal(true);
    setUploading(true);

    try {
      const res = await uploadCandidateResume(id, file);

      console.log("Upload Success:", res.data);

      // 🔄 Refresh resume list
      refreshResume && refreshResume();

    } catch (error) {
      console.error("Upload Error:", error);
      alert("Upload failed");
    } finally {
      setUploading(false);

      setTimeout(() => {
        setShowUploadModal(false);
        setCandidateName("");
      }, 1500);
    }
  };

  // 🔥 File select
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    validateAndUpload(file);
  };

  // 🔥 Drag events
  const handleDragEnter = (e) => {
    e.preventDefault();
    dragCounter.current++;
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    validateAndUpload(file);
  };

  const handleCloseModal = () => {
    setShowUploadModal(false);
    setCandidateName("");
  };

  return (
    <>
      {/* DRAG WRAPPER */}
      <div
        onDragEnter={handleDragEnter}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={handleDragLeave}
      >
        <div className={style.activitiesContainer}>
          <div className={style.title}>
            Resumes Related to this Candidate
          </div>

          {/* ✅ TABLE */}
          {data?.length > 0 ? (
            <div className={style.tableContainer}>
              {/* Upload Button */}
              <div className="d-flex justify-content-end mb-3">
                <button
                  className={style.uploadButtonTop}
                  onClick={handleUploadClick}
                  disabled={uploading}
                >
                  <MdOutlineFileUpload />
                  <b>{uploading ? "Uploading..." : "Upload Resume"}</b>
                </button>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.rtf"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
              </div>

              {/* TABLE */}
              <table className={style.candidateTable}>
                <thead>
                  <tr className={style.trWrapper}>
                    <th>File Name</th>
                    <th>File Type</th>
                    <th>Created On</th>
                    <th>Uploaded By</th>
                  </tr>
                </thead>

                <tbody className={style.tableBody}>
                  {data.map((doc) => (
                    <tr key={doc.id}>
                      <td
                        className={style.candidateFileName}
                        onClick={() =>
                          window.open(doc.doc_link, "_blank")
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <GrDocumentText />
                          {doc.doc_title || "Resume"}
                        </div>
                      </td>

                      <td>
                        {doc.doc_title
                          ?.split(".")
                          .pop()
                          ?.toUpperCase() || "FILE"}
                      </td>

                      <td>{<Date_Date dateValue={doc.updated_time}/> }</td>

                      <td>
                        <div className="d-flex align-items-center">
                          <span className={style.assignedTo}>
                            <span
                              className={style.assignedToFirstLetter}
                            >
                              {doc.user_name
                                ?.charAt(0)
                                ?.toUpperCase() || "?"}
                            </span>
                            <span className="me-1">
                              {doc.user_name || "Unknown"}
                            </span>
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            /* ❌ EMPTY STATE */
            <div className={style.activitiesBodyContainer}>
              <h6>
                <b>No Resumes Found</b>
              </h6>

              <span>
                This candidate Resume has not been added.
              </span>

              <div className={style.boxModel}>
                <div
                  className={style.box}
                  onClick={handleUploadClick}
                >
                  <LuDownload className={style.downloadArrow} />
                  <span className={style.boxTitle}>
                    Drag & drop
                  </span>
                  <div className={style.boxpara}>
                    Drag a file here or click to browse
                  </div>
                </div>

                <div className="d-flex justify-content-end mt-3">
                  <button
                    className={style.uploadButtonTop}
                    onClick={handleUploadClick}
                    disabled={uploading}
                  >
                    <MdOutlineFileUpload />
                    <b>
                      {uploading
                        ? "Uploading..."
                        : "Upload Resume"}
                    </b>
                  </button>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.rtf"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 🔥 DRAG OVERLAY */}
      {isDragging && (
        <div className={style.dragOverlay}>
          <div
            className={style.dragBox}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <LuUpload className={style.dragIcon} />
            <h4>Drop your file here</h4>
            <p>Upload PDF, DOC, DOCX (Max: 1.5MB)</p>
          </div>
        </div>
      )}

      {/* 🔥 SUCCESS MODAL */}
      {showUploadModal && (
        <MyModal>
          <ModalContainer zIndex={1001}>
            <SuccessComponent
              name={`${candidateName} is Uploading...`}
              onclose={handleCloseModal}
              msgContent="Please wait, almost completed"
               boldContent="Don’t close the tab"
              primaryHeading={false}
            />
          </ModalContainer>
        </MyModal>
      )}
    </>
  );
}

export default CandidateResume;